import React, { useState, useEffect } from "react";
import {
  Box, Typography, Paper, Chip, Button, Table, TableHead, TableRow, TableCell, 
  TableBody, TextField, InputAdornment, IconButton, MenuItem, Select, FormControl,
  Stack, Collapse, TableSortLabel, TablePagination, Alert, CircularProgress,
  Autocomplete, Skeleton, Grid, SelectChangeEvent, Tooltip
} from "@mui/material";
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import {
  Search as SearchIcon, Visibility as VisibilityIcon, Download as DownloadIcon,
  FilterList as FilterListIcon, Person as PersonIcon, CorporateFare as JobIcon,
} from "@mui/icons-material";
import {
  getJobsList, getJobById, getCandidatesByJobId, updateCandidateStatus,
  getCandidateCV, JobDetail, Candidate, JobLS
} from "../Services/jobCandidateService";

// Types
type Order = 'asc' | 'desc';
type OrderBy = keyof Pick<Candidate, 'name' | 'email' | 'status' | 'submitted'>;

// Define the evaluation criteria with weights
const evaluationCriteria = [
  { id: 'qualification', label: 'Qualifications', weight: '20%' },
  { id: 'experience', label: 'Experience', weight: '40%' },
  { id: 'technicalSkill', label: 'Technical Skill', weight: '20%' },
  { id: 'sectorExperience', label: 'Sector Experience', weight: '10%' },
  { id: 'additional', label: 'Additional', weight: '10%' },
];

const CandidateTrackingPage: React.FC = () => {
  // State
  const [jobsList, setJobsList] = useState<JobLS[]>([]);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [jobDetail, setJobDetail] = useState<JobDetail | null>(null);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [loading, setLoading] = useState({
    jobsList: true, jobDetail: false, candidates: false, downloadCV: false
  });
  const [error, setError] = useState({
    jobsList: '', jobDetail: '', candidates: '', downloadCV: ''
  });
  
  // UI state
  const [search, setSearch] = useState("");
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<OrderBy>('submitted');
  const [statusFilter, setStatusFilter] = useState<string>("All");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [showFilters, setShowFilters] = useState(false);
  const [downloadingCandidateId, setDownloadingCandidateId] = useState<string | null>(null);

  // Fetch jobs list on component mount
  useEffect(() => {
    const fetchJobsList = async () => {
      try {
        setLoading(prev => ({ ...prev, jobsList: true }));
        setError(prev => ({ ...prev, jobsList: '' }));
        const jobsData = await getJobsList();
        setJobsList(jobsData);
      } catch (err) {
        setError(prev => ({ 
          ...prev, jobsList: err instanceof Error ? err.message : 'Failed to fetch jobs list' 
        }));
      } finally {
        setLoading(prev => ({ ...prev, jobsList: false }));
      }
    };
    fetchJobsList();
  }, []);

  // Fetch job details when selected job changes
  useEffect(() => {
    if (!selectedJobId) {
      setJobDetail(null);
      setCandidates([]);
      return;
    }
    
    const fetchJobDetail = async () => {
      try {
        setLoading(prev => ({ ...prev, jobDetail: true }));
        setError(prev => ({ ...prev, jobDetail: '' }));
        const jobData = await getJobById(selectedJobId);
        setJobDetail(jobData);
      } catch (err) {
        setError(prev => ({ 
          ...prev, jobDetail: err instanceof Error ? err.message : 'Failed to fetch job details' 
        }));
        setJobDetail(null);
      } finally {
        setLoading(prev => ({ ...prev, jobDetail: false }));
      }
    };
    fetchJobDetail();
  }, [selectedJobId]);

  // Fetch candidates when job detail changes
  useEffect(() => {
    if (!jobDetail?.jobId) return;
    
    const fetchCandidates = async () => {
      try {
        setLoading(prev => ({ ...prev, candidates: true }));
        setError(prev => ({ ...prev, candidates: '' }));
        const candidatesData = await getCandidatesByJobId(jobDetail.jobId);
        setCandidates(Array.isArray(candidatesData) ? candidatesData : []);
      } catch (err) {
        setError(prev => ({ 
          ...prev, candidates: err instanceof Error ? err.message : 'Failed to fetch candidates' 
        }));
        setCandidates([]);
      } finally {
        setLoading(prev => ({ ...prev, candidates: false }));
      }
    };
    fetchCandidates();
  }, [jobDetail?.jobId]);

  // Utility functions
  const getComparator = (order: Order, orderBy: OrderBy) => 
    order === 'desc'
      ? (a: Candidate, b: Candidate) => descendingComparator(a, b, orderBy)
      : (a: Candidate, b: Candidate) => -descendingComparator(a, b, orderBy);

  const descendingComparator = <T,>(a: T, b: T, orderBy: keyof T): number => {
    const aValue = a[orderBy];
    const bValue = b[orderBy];
    if (!aValue && bValue) return 1;
    if (aValue && !bValue) return -1;
    if (!aValue && !bValue) return 0;
    return bValue < aValue ? -1 : bValue > aValue ? 1 : 0;
  };
  
  const getStatusColor = (status: string = ''): "primary" | "success" | "error" | "info" | "warning" | "default" => {
    switch (status.toLowerCase()) {
      case 'shortlisted': return 'primary';
      case 'finalized': return 'success';
      case 'rejected': return 'error';
      case 'applied': return 'info';
      case 'in review': return 'warning';
      case 'open': return 'primary';
      default: return 'default';
    }
  };

  // Mock score data - In a real application, this would come from the candidate data
  const getEvaluationScore = (candidateId: string, criteriaId: string): number => {
    // Mock implementation - in real application, this would access actual scores from candidate data
    const hash = candidateId.charCodeAt(0) + criteriaId.charCodeAt(0);
    return Math.floor((hash % 5) + 1); // Return a score between 1-5
  };

  // Apply filters and sorting
  const filteredCandidates = React.useMemo(() => 
    candidates
      .filter(c => {
        const matchesSearch = 
          (c.name?.toLowerCase() || '').includes(search.toLowerCase()) || 
          (c.email?.toLowerCase() || '').includes(search.toLowerCase());
        const matchesStatus = statusFilter === "All" || c.status === statusFilter;
        return matchesSearch && matchesStatus;
      })
      .sort(getComparator(order, orderBy))
      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage), 
    [candidates, search, statusFilter, order, orderBy, page, rowsPerPage]
  );

  const filteredCount = React.useMemo(() => 
    candidates.filter(c => {
      const matchesSearch = 
        (c.name?.toLowerCase() || '').includes(search.toLowerCase()) || 
        (c.email?.toLowerCase() || '').includes(search.toLowerCase());
      const matchesStatus = statusFilter === "All" || c.status === statusFilter;
      return matchesSearch && matchesStatus;
    }).length,
    [candidates, search, statusFilter]
  );

  // Event handlers
  const handleRequestSort = (property: OrderBy) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const resetFilters = () => {
    setStatusFilter("All");
    setSearch("");
    setPage(0);
  };

  const handleAutocompleteJobChange = (job: JobLS | null) => {
    setSelectedJobId(job?.jobId || null);
    setExpandedRow(null);
    resetFilters();
  };
  
  const handleStatusChange = async (candidateId: string, newStatus: string) => {
    try {
      await updateCandidateStatus(candidateId, newStatus);
      setCandidates(prev => 
        prev.map(c => c.id === candidateId ? { ...c, status: newStatus } : c)
      );
    } catch (err) {
      setError(prev => ({ 
        ...prev, candidates: err instanceof Error ? err.message : 'Failed to update candidate status' 
      }));
    }
  };

  const handleDownloadCV = async (candidateId: string, candidateName: string) => {
    try {
      setDownloadingCandidateId(candidateId);
      setLoading(prev => ({ ...prev, downloadCV: true }));
      
      const cvBlob = await getCandidateCV(candidateId);
      const url = window.URL.createObjectURL(cvBlob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `CV_${candidateName || candidateId}.pdf`);
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
    } catch (err) {
      setError(prev => ({ 
        ...prev, downloadCV: err instanceof Error ? err.message : 'Failed to download CV' 
      }));
    } finally {
      setLoading(prev => ({ ...prev, downloadCV: false }));
      setDownloadingCandidateId(null);
    }
  };

  // Score visualization helper
  const renderScore = (score: number) => {
    const scoreColor = score >= 4 ? 'success.main' : 
                      score >= 3 ? 'info.main' : 
                      score >= 2 ? 'warning.main' : 'error.main';
    
    return (
      <Typography variant="body2" fontWeight="medium" color={scoreColor} sx={{ textAlign: 'center' }}>
        {score}
      </Typography>
    );
  };

  // Loading state for the entire page
  if (loading.jobsList && jobsList.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="rectangular" height={100} sx={{ mb: 2, borderRadius: 1 }} />
        <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 1 }} />
      </Box>
    );
  }

  // Error state for jobs with retry option
  if (error.jobsList && jobsList.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
        <Paper sx={{ p: 4, maxWidth: 500, textAlign: 'center' }}>
          <Typography variant="h5" gutterBottom>Failed to Load Jobs</Typography>
          <Typography variant="body1" color="text.secondary" paragraph>{error.jobsList}</Typography>
          <Button variant="contained" onClick={() => window.location.reload()}>Retry</Button>
        </Paper>
      </Box>
    );
  }

  // No jobs available state
  if (jobsList.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
        <Paper sx={{ p: 4, maxWidth: 500, textAlign: 'center' }}>
          <Typography variant="h5" gutterBottom>No Jobs Available</Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            There are currently no jobs in the system.
          </Typography>
          <Button variant="contained">Create Job</Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Job Selection */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 1, boxShadow: 1 }}>
        <FormControl fullWidth sx={{ mb: 3 }}>
          <Autocomplete
            options={jobsList}
            getOptionLabel={(job) => job?.jobTitle ? `${job.jobTitle} (${job.status})` : ''}
            value={jobsList.find(job => job.jobId === selectedJobId) || null}
            onChange={(_, newValue) => handleAutocompleteJobChange(newValue)}
            isOptionEqualToValue={(option, value) => option.jobId === value.jobId}
            loading={loading.jobsList}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Job"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <InputAdornment position="start"><JobIcon /></InputAdornment>
                      {params.InputProps.startAdornment}
                    </>
                  )
                }}
              />
            )}
          />
        </FormControl>

        {loading.jobDetail ? (
          <Box sx={{ my: 2 }}>
            <Skeleton variant="text" height={40} width="60%" />
            <Skeleton variant="text" height={20} width="80%" />
            <Skeleton variant="text" height={20} width="40%" />
          </Box>
        ) : error.jobDetail ? (
          <Alert severity="error">{error.jobDetail}</Alert>
        ) : jobDetail ? (
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Typography variant="h5" fontWeight="medium" gutterBottom>
                {jobDetail.jobTitle || 'Untitled Job'}
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {jobDetail.AdditionalInformation || 'No additional information available'}
              </Typography>
              
              <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mb: 2 }}>
                {jobDetail.location && (
                  <Chip label={`Location: ${jobDetail.location}`} size="small" variant="outlined" />
                )}
                {jobDetail.deadline && (
                  <Chip label={`Deadline: ${jobDetail.deadline}`} size="small" variant="outlined" />
                )}
                {jobDetail.status && (
                  <Chip label={jobDetail.status} color={getStatusColor(jobDetail.status)} size="small" />
                )}
              </Stack>
              
              {jobDetail.screeningQuestions && jobDetail.screeningQuestions.length > 0 && (
                <>
                  <Typography variant="subtitle1" fontWeight="medium" sx={{ mt: 3, mb: 1 }}>
                    Screening Questions
                  </Typography>
                  {jobDetail.screeningQuestions.map((question, index) => (
                    <Typography key={index} variant="body2" sx={{ mb: 1 }}>
                      {index + 1}. {question}
                    </Typography>
                  ))}
                </>
              )}
              <>
                <Typography variant="subtitle1" fontWeight="medium" sx={{ mt: 3, mb: 1 }}>
                  Evaluation Criteria
                </Typography>
                <List dense disablePadding>
                  {evaluationCriteria.map((item, index) => (
                    <ListItem key={index} sx={{ px: 0, py: 0.5, display: 'flex', justifyContent: 'space-between' }}>
                      <ListItemText primary={`${index + 1}. ${item.label}`} />
                      <Chip label={item.weight} color="default" size="small" sx={{ minWidth: 60 }} />
                    </ListItem>
                  ))}
                </List>
              </>
            </Grid>
            
            <Grid item xs={12} md={4}>
              {jobDetail.stats && (
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                    Application Stats
                  </Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Typography variant="body2">Total</Typography>
                      <Typography variant="h6">{jobDetail.stats.total || 0}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="primary.main">Shortlisted</Typography>
                      <Typography variant="h6" color="primary.main">{jobDetail.stats.shortlisted || 0}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="success.main">Finalized</Typography>
                      <Typography variant="h6" color="success.main">{jobDetail.stats.finalized || 0}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="error.main">Rejected</Typography>
                      <Typography variant="h6" color="error.main">{jobDetail.stats.rejected || 0}</Typography>
                    </Grid>
                  </Grid>
                </Paper>
              )}
            </Grid>
          </Grid>
        ) : (
          <Alert severity="info">Please select a job to view details</Alert>
        )}
      </Paper>

      {/* Candidate Data Table */}
      <Paper sx={{ borderRadius: 1, boxShadow: 1 }}>
        <Box sx={{ p: 2 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems="center" spacing={2} sx={{ mb: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="h6">Candidates</Typography>
              <Chip label={candidates.length} size="small" />
              {loading.candidates && <CircularProgress size={16} sx={{ ml: 1 }} />}
            </Stack>
            
            <Stack direction="row" spacing={1}>
              <TextField
                placeholder="Search candidates"
                size="small"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                InputProps={{
                  startAdornment: (<InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>),
                }}
                sx={{ width: { xs: '100%', sm: 200 } }}
                disabled={loading.candidates || candidates.length === 0}
              />
              
              <Button 
                variant={showFilters ? "contained" : "outlined"}
                size="small"
                color="primary"
                startIcon={<FilterListIcon />}
                onClick={() => setShowFilters(!showFilters)}
                disabled={loading.candidates || candidates.length === 0}
              >
                {showFilters ? "Hide Filters" : "Filters"}
              </Button>
            </Stack>
          </Stack>
          
          {error.candidates && <Alert severity="error" sx={{ mb: 2 }}>{error.candidates}</Alert>}
          {error.downloadCV && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(prev => ({ ...prev, downloadCV: '' }))}>
              {error.downloadCV}
            </Alert>
          )}
          
          {/* Filters section */}
          <Collapse in={showFilters}>
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <Select
                    value={statusFilter}
                    onChange={(e: SelectChangeEvent) => { setStatusFilter(e.target.value); setPage(0); }}
                  >
                    <MenuItem value="All">All Statuses</MenuItem>
                    <MenuItem value="Applied">Applied</MenuItem>
                    <MenuItem value="Shortlisted">Shortlisted</MenuItem>
                    <MenuItem value="Finalized">Finalized</MenuItem>
                    <MenuItem value="Rejected">Rejected</MenuItem>
                  </Select>
                </FormControl>
                <Button variant="outlined" size="small" onClick={resetFilters}>Reset</Button>
              </Stack>
            </Paper>
          </Collapse>
          
          {/* Data table */}
          {loading.candidates ? (
            <Box sx={{ py: 4 }}>
              {[1, 2, 3].map(i => (
                <Skeleton key={i} variant="rectangular" height={40} sx={{ mb: 1, borderRadius: 0.5 }} />
              ))}
            </Box>
          ) : candidates.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Typography variant="subtitle1" color="text.secondary">
                No candidates found for this job
              </Typography>
            </Box>
          ) : filteredCandidates.length === 0 ? (
            <Alert severity="info">No candidates match your search criteria</Alert>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'name'}
                      direction={orderBy === 'name' ? order : 'asc'}
                      onClick={() => handleRequestSort('name')}
                    >Name</TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'status'}
                      direction={orderBy === 'status' ? order : 'asc'}
                      onClick={() => handleRequestSort('status')}
                    >Status</TableSortLabel>
                  </TableCell>
                  
                  {/* Evaluation Criteria Columns */}
                  {evaluationCriteria.map((criteria) => (
                    <TableCell key={criteria.id} align="center" sx={{ px: 1 }}>
                      <Tooltip title={criteria.label} placement="top" arrow>
                        <Typography variant="body2" noWrap>
                          {`C${criteria.label.charAt(0)}`} <Typography variant="caption" color="text.secondary">({criteria.weight})</Typography>
                        </Typography>
                      </Tooltip>
                    </TableCell>
                  ))}
                  
                  <TableCell align="right" sx={{ width: 100 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredCandidates.map((candidate) => (
                  <React.Fragment key={candidate.id}>
                    <TableRow hover>
                      <TableCell sx={{ py: 1 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <PersonIcon fontSize="small" color="action" />
                          <Typography variant="body2" noWrap sx={{ maxWidth: 160 }}>
                            {candidate.name || 'Unknown'}
                            <Typography variant="caption" display="block" color="text.secondary">
                              {candidate.email || 'No email'}
                            </Typography>
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ py: 1 }}>
                        <Chip label={candidate.status || 'No Status'} color={getStatusColor(candidate.status)} size="small" />
                      </TableCell>
                      
                      {/* Evaluation Criteria Scores */}
                      {evaluationCriteria.map((criteria) => (
                        <TableCell key={criteria.id} align="center" sx={{ py: 1, px: 1 }}>
                          {renderScore(getEvaluationScore(candidate.id, criteria.id))}
                        </TableCell>
                      ))}
                      
                      <TableCell align="right" sx={{ py: 1 }}>
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          {candidate.cvFilePath && (
                            <IconButton
                              size="small"
                              onClick={() => handleDownloadCV(candidate.id, candidate.name || '')}
                              disabled={loading.downloadCV && downloadingCandidateId === candidate.id}
                            >
                              {loading.downloadCV && downloadingCandidateId === candidate.id ? 
                                <CircularProgress size={16} /> : <DownloadIcon fontSize="small" />}
                            </IconButton>
                          )}
                          <IconButton 
                            size="small"
                            onClick={() => setExpandedRow(expandedRow === candidate.id ? null : candidate.id)}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={8} sx={{ p: 0 }}>
                        <Collapse in={expandedRow === candidate.id} timeout="auto" unmountOnExit>
                          <Box sx={{ p: 2, backgroundColor: 'rgba(0, 0, 0, 0.02)' }}>
                            <Grid container spacing={2}>
                              <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" gutterBottom>Screening Answers</Typography>
                                
                                {jobDetail?.screeningQuestions ? (
                                  jobDetail.screeningQuestions.map((question, idx) => {
                                    let answer = "No answer provided";
                                    
                                    if (candidate.answers && Array.isArray(candidate.answers) && candidate.answers.length > 0) {
                                      try {
                                        const answerString = candidate.answers[0];
                                        const parsedAnswers = JSON.parse(answerString);
                                        answer = Array.isArray(parsedAnswers) ? 
                                          parsedAnswers[idx] || "No answer provided" : 
                                          idx === 0 ? answerString : "No answer provided";
                                      } catch (e) {
                                        answer = "Error parsing answer";
                                      }
                                    }

                                    return (
                                      <Box key={idx} sx={{ mb: 1 }}>
                                        <Typography variant="body2" color="text.secondary">
                                          Q{idx + 1}: {question}
                                        </Typography>
                                        <Typography variant="body2" sx={{ pl: 2, pt: 0.5 }}>{answer}</Typography>
                                      </Box>
                                    );
                                  })
                                ) : (
                                  <Typography variant="body2" color="text.secondary">
                                    No screening questions available.
                                  </Typography>
                                )}
                              </Grid>
                              
                              <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" gutterBottom>Evaluation Details</Typography>
                                
                                <Stack spacing={1}>
                                  {evaluationCriteria.map((criteria) => {
                                    const score = getEvaluationScore(candidate.id, criteria.id);
                                    return (
                                      <Box key={criteria.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Typography variant="body2">{criteria.label} ({criteria.weight})</Typography>
                                        <Chip 
                                          label={score} 
                                          size="small" 
                                          color={
                                            score >= 4 ? 'success' : 
                                            score >= 3 ? 'info' : 
                                            score >= 2 ? 'warning' : 'error'
                                          }
                                        />
                                      </Box>
                                    );
                                  })}
                                </Stack>
                              </Grid>
                            </Grid>
                            
                            <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                              <Button size="small" variant="outlined" color="primary"
                                onClick={() => handleStatusChange(candidate.id, "Shortlisted")}
                                disabled={candidate.status === "Shortlisted"}>
                                Shortlist
                              </Button>
                              <Button size="small" variant="outlined" color="success"
                                onClick={() => handleStatusChange(candidate.id, "Finalized")}
                                disabled={candidate.status === "Finalized"}>
                                Finalize
                              </Button>
                              <Button size="small" variant="outlined" color="error"
                                onClick={() => handleStatusChange(candidate.id, "Rejected")}
                                disabled={candidate.status === "Rejected"}>
                                Reject
                              </Button>
                            </Stack>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          )}
          
          {candidates.length > 0 && (
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={filteredCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
            />
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default CandidateTrackingPage;